import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Heart,
  BookOpen,
  Lightbulb,
  Target,
  MessageCircle,
  Eye,
  Zap,
  Save
} from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

// Text question component - MUST be outside main component to prevent re-renders
const TextQuestion = ({ value, onChange, label, placeholder, hint, rows = 4 }) => (
  <div className="space-y-2">
    <label className="text-base font-medium text-white">{label}</label>
    <Textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-400 text-base resize-none"
    />
    {hint && <p className="text-xs text-zinc-400">{hint}</p>}
  </div>
);

function IkonsPostMatch() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const totalSteps = 7;

  // Section definitions
  const sections = [
    { step: 1, title: 'Fecha', icon: null },
    { step: 2, title: 'Descarga', icon: Heart },
    { step: 3, title: 'Hechos', icon: Eye },
    { step: 4, title: 'Cierre', icon: Zap },
    { step: 5, title: 'Decisión 1', icon: BookOpen },
    { step: 6, title: 'Patrón', icon: Lightbulb },
    { step: 7, title: 'Resumen', icon: Check },
  ];

  // Mental Recovery State
  const [mentalRecovery, setMentalRecovery] = useState({
    match_date: new Date().toISOString().split('T')[0],
    current_feeling: '',
    facts: '',
    story: '',
    learning: '',
    closing_phrase: '',
    work_focus: ''
  });

  // Single Decision (simplified)
  const [decision, setDecision] = useState({
    situation: '',
    decision_made: '',
    was_correct: false,
    reason: ''
  });

  // Pattern & Adjustment
  const [pattern, setPattern] = useState({
    pattern_detected: '',
    next_match_adjustment: ''
  });

  // Auto-save draft to localStorage
  const saveDraft = useCallback(() => {
    const draft = { mentalRecovery, decision, pattern };
    localStorage.setItem(`postmatch_draft_${mentalRecovery.match_date}`, JSON.stringify(draft));
    setLastSaved(new Date());
  }, [mentalRecovery, decision, pattern]);

  // Load draft on mount
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const savedDraft = localStorage.getItem(`postmatch_draft_${today}`);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        setMentalRecovery(draft.mentalRecovery || mentalRecovery);
        setDecision(draft.decision || decision);
        setPattern(draft.pattern || pattern);
        toast.info('Borrador recuperado');
      } catch (e) {
        console.error('Error loading draft:', e);
      }
    }
  }, []);

  // Auto-save every 30 seconds
  useEffect(() => {
    const interval = setInterval(saveDraft, 30000);
    return () => clearInterval(interval);
  }, [saveDraft]);

  const handleNext = () => {
    saveDraft();
    if (step < totalSteps) setStep(step + 1);
  };

  const handleBack = () => {
    saveDraft();
    if (step > 1) setStep(step - 1);
    else navigate('/ikons');
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Save mental recovery
      await api.post('/ikons/mental-recovery', mentalRecovery);
      
      // Save decision diary
      await api.post('/ikons/decision-diary', {
        match_date: mentalRecovery.match_date,
        decisions: [decision],
        pattern_detected: pattern.pattern_detected,
        next_match_adjustment: pattern.next_match_adjustment
      });

      // Clear draft
      localStorage.removeItem(`postmatch_draft_${mentalRecovery.match_date}`);
      
      toast.success('Post-partido guardado');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-[#050505]" data-testid="ikons-post-match">
        {/* Header */}
        <div className="sticky top-14 lg:top-0 z-30 bg-[#050505]/95 backdrop-blur-xl border-b border-white/10 px-4 py-3">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center justify-between mb-3">
              <button onClick={handleBack} className="p-2 -ml-2 text-zinc-400 active:scale-95">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="text-center">
                <span className="text-xs text-zinc-400 uppercase tracking-wider">Post-Partido</span>
                <p className="text-sm text-white font-medium">{sections[step - 1]?.title}</p>
              </div>
              <div className="w-9" />
            </div>
            
            {/* Progress bar */}
            <div className="flex gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-all ${
                    i < step ? 'bg-blue-500' : 'bg-zinc-800'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Auto-save indicator */}
        {lastSaved && (
          <div className="px-4 pt-4 max-w-lg mx-auto">
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <Save className="w-3 h-3" />
              <span>Guardado: {lastSaved.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="px-4 py-6 max-w-lg mx-auto pb-32">
          
          {/* Step 1: Date */}
          {step === 1 && (
            <div className="space-y-6 animate-scale-in">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Oswald' }}>
                  CIERRE DE PARTIDO
                </h1>
                <p className="text-zinc-400">Procesa el partido y aprende de él</p>
              </div>

              <div className="card-dark p-6">
                <label className="text-sm text-zinc-400 mb-3 block">¿Cuándo jugaste?</label>
                <Input
                  type="date"
                  value={mentalRecovery.match_date}
                  onChange={(e) => setMentalRecovery({ ...mentalRecovery, match_date: e.target.value })}
                  className="h-14 bg-zinc-900 border-zinc-800 text-white text-lg"
                />
              </div>

              {/* Quick info card */}
              <div className="card-dark p-4 border-l-4 border-blue-500">
                <p className="text-sm text-zinc-300">
                  <strong className="text-blue-400">Tip:</strong> Completa esto en los primeros 30 minutos post-partido para mejor retención emocional.
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Emotional Discharge */}
          {step === 2 && (
            <div className="space-y-6 animate-scale-in">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                    <Heart className="w-5 h-5 text-red-400" />
                  </div>
                  <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald' }}>
                    DESCARGA EMOCIONAL
                  </h1>
                </div>
                <p className="text-zinc-400">Sé honesto. Este es tu espacio.</p>
              </div>

              <TextQuestion
                value={mentalRecovery.current_feeling}
                onChange={(v) => setMentalRecovery({ ...mentalRecovery, current_feeling: v })}
                label="¿Qué estás sintiendo ahora mismo?"
                placeholder="Frustración, satisfacción, rabia, alegría, alivio..."
                hint="No filtres. Escribe lo primero que te viene."
                rows={6}
              />
            </div>
          )}

          {/* Step 3: Facts vs Story */}
          {step === 3 && (
            <div className="space-y-6 animate-scale-in">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Eye className="w-5 h-5 text-blue-400" />
                  </div>
                  <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald' }}>
                    HECHOS VS HISTORIA
                  </h1>
                </div>
                <p className="text-zinc-400">Separa lo que pasó de lo que te cuentas</p>
              </div>

              <div className="card-dark p-4 border-l-4 border-blue-400">
                <TextQuestion
                  value={mentalRecovery.facts}
                  onChange={(v) => setMentalRecovery({ ...mentalRecovery, facts: v })}
                  label="Hechos (Objetivo)"
                  placeholder="Ej: 'Fallé un pase en el minuto 30'"
                  hint="Solo hechos observables, sin interpretación"
                  rows={3}
                />
              </div>

              <div className="card-dark p-4 border-l-4 border-purple-400">
                <TextQuestion
                  value={mentalRecovery.story}
                  onChange={(v) => setMentalRecovery({ ...mentalRecovery, story: v })}
                  label="Historia (Interpretación)"
                  placeholder="Ej: 'Jugué horrible, no estoy al nivel'"
                  hint="Lo que te cuentas sobre esos hechos"
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Step 4: Learning + Closure */}
          {step === 4 && (
            <div className="space-y-6 animate-scale-in">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-green-400" />
                  </div>
                  <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald' }}>
                    CIERRE MENTAL
                  </h1>
                </div>
                <p className="text-zinc-400">Un aprendizaje claro vale más que diez confusos</p>
              </div>

              <TextQuestion
                value={mentalRecovery.learning}
                onChange={(v) => setMentalRecovery({ ...mentalRecovery, learning: v })}
                label="UN aprendizaje clave de este partido"
                placeholder="¿Qué te llevas?"
                rows={3}
              />

              <div className="space-y-2">
                <label className="text-base font-medium text-white">Frase de cierre</label>
                <Input
                  value={mentalRecovery.closing_phrase}
                  onChange={(e) => setMentalRecovery({ ...mentalRecovery, closing_phrase: e.target.value })}
                  placeholder="Ej: 'Este partido ya terminó. Ahora a trabajar.'"
                  className="h-14 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-400"
                />
                <p className="text-xs text-zinc-400">Una frase definitiva que cierre el capítulo</p>
              </div>

              <TextQuestion
                value={mentalRecovery.work_focus}
                onChange={(v) => setMentalRecovery({ ...mentalRecovery, work_focus: v })}
                label="¿En qué trabajarás esta semana?"
                placeholder="Conecta con tu desarrollo"
                rows={2}
              />
            </div>
          )}

          {/* Step 5: One Key Decision */}
          {step === 5 && (
            <div className="space-y-6 animate-scale-in">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-orange-400" />
                  </div>
                  <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald' }}>
                    UNA DECISIÓN CLAVE
                  </h1>
                </div>
                <p className="text-zinc-400">Analiza la decisión más importante del partido</p>
              </div>

              <TextQuestion
                value={decision.situation}
                onChange={(v) => setDecision({ ...decision, situation: v })}
                label="¿Qué pasó? (Situación)"
                placeholder="Describe el momento de juego"
                rows={3}
              />

              <div className="space-y-2">
                <label className="text-base font-medium text-white">¿Qué decidiste?</label>
                <Input
                  value={decision.decision_made}
                  onChange={(e) => setDecision({ ...decision, decision_made: e.target.value })}
                  placeholder="La acción que tomaste"
                  className="h-14 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-400"
                />
              </div>

              <div className="card-dark p-4">
                <div className="flex items-center justify-between">
                  <label className="text-base font-medium text-white">¿Fue la decisión correcta?</label>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-medium ${!decision.was_correct ? 'text-red-400' : 'text-zinc-400'}`}>No</span>
                    <Switch
                      checked={decision.was_correct}
                      onCheckedChange={(checked) => setDecision({ ...decision, was_correct: checked })}
                      className="data-[state=checked]:bg-green-500"
                    />
                    <span className={`text-sm font-medium ${decision.was_correct ? 'text-green-400' : 'text-zinc-400'}`}>Sí</span>
                  </div>
                </div>
              </div>

              <TextQuestion
                value={decision.reason}
                onChange={(v) => setDecision({ ...decision, reason: v })}
                label="¿Por qué?"
                placeholder="Explica tu razonamiento"
                rows={3}
              />
            </div>
          )}

          {/* Step 6: Pattern + Adjustment */}
          {step === 6 && (
            <div className="space-y-6 animate-scale-in">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-amber-400" />
                  </div>
                  <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald' }}>
                    PATRÓN Y AJUSTE
                  </h1>
                </div>
                <p className="text-zinc-400">Los patrones son más valiosos que las decisiones individuales</p>
              </div>

              <TextQuestion
                value={pattern.pattern_detected}
                onChange={(v) => setPattern({ ...pattern, pattern_detected: v })}
                label="¿Qué patrón detectas?"
                placeholder="Ej: 'Juego seguro cuando debería progresar'"
                hint="¿Qué tipo de decisiones se repiten en tus partidos?"
                rows={4}
              />

              <div className="card-dark p-4 border-l-4 border-green-500">
                <TextQuestion
                  value={pattern.next_match_adjustment}
                  onChange={(v) => setPattern({ ...pattern, next_match_adjustment: v })}
                  label="Ajuste para el próximo partido"
                  placeholder="UNA acción específica que harás diferente"
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Step 7: Summary */}
          {step === 7 && (
            <div className="space-y-6 animate-scale-in">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Oswald' }}>
                  RESUMEN
                </h1>
                <p className="text-zinc-400">Revisa antes de guardar</p>
              </div>

              {/* Key Learning */}
              {mentalRecovery.learning && (
                <div className="card-dark p-4">
                  <p className="text-xs text-green-400 uppercase tracking-wider mb-1">Aprendizaje Clave</p>
                  <p className="text-white text-sm">{mentalRecovery.learning}</p>
                </div>
              )}

              {/* Closing Phrase */}
              {mentalRecovery.closing_phrase && (
                <div className="card-dark p-4 border-l-4 border-blue-500">
                  <p className="text-xs text-blue-400 uppercase tracking-wider mb-1">Cierre Mental</p>
                  <p className="text-white text-sm italic">"{mentalRecovery.closing_phrase}"</p>
                </div>
              )}

              {/* Pattern */}
              {pattern.pattern_detected && (
                <div className="card-dark p-4">
                  <p className="text-xs text-amber-400 uppercase tracking-wider mb-1">Patrón Detectado</p>
                  <p className="text-white text-sm">{pattern.pattern_detected}</p>
                </div>
              )}

              {/* Next Adjustment */}
              {pattern.next_match_adjustment && (
                <div className="card-dark p-4 border-l-4 border-green-500">
                  <p className="text-xs text-green-400 uppercase tracking-wider mb-1">Ajuste Próximo Partido</p>
                  <p className="text-white text-sm">{pattern.next_match_adjustment}</p>
                </div>
              )}

              {/* Motivational Quote */}
              <div className="card-dark p-4 text-center">
                <p className="text-sm text-zinc-400 italic">
                  "No te define un partido. Te define cómo lo procesas."
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Fixed Bottom Action */}
        <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 p-4 bg-[#050505]/95 backdrop-blur-xl border-t border-white/10 safe-area-bottom">
          <div className="max-w-lg mx-auto flex gap-3">
            {step > 1 && (
              <Button
                onClick={handleBack}
                variant="outline"
                className="h-14 px-6 border-zinc-700 text-white hover:bg-zinc-800"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            
            {step < totalSteps ? (
              <Button
                onClick={handleNext}
                className="flex-1 h-14 bg-blue-500 hover:bg-blue-600 text-white font-bold text-base uppercase tracking-wide active:scale-95 transition-transform"
              >
                Continuar
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 h-14 bg-blue-500 hover:bg-blue-600 text-white font-bold text-base uppercase tracking-wide active:scale-95 transition-transform"
              >
                {loading ? 'Guardando...' : 'Guardar Post-Partido'}
                <Check className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default IkonsPostMatch;
